import { at } from './indexed';

export const ZONE_MODELS = ['power', 'hr_lthr', 'hr_max', 'pace'] as const;
export type ZoneModel = (typeof ZONE_MODELS)[number];

export interface ZoneTime {
  model: ZoneModel;
  bounds: number[];
  seconds: number[];
}

// Lower bounds of Z2 and up, in whole percent of the threshold; a top zone written as "> X" starts at X + 1.
// Pace bounds are percent of threshold pace in s/km, so they fall: a faster (smaller) pace is a higher zone.
const ZONE_PERCENTS: Record<ZoneModel, readonly number[]> = {
  power: [56, 76, 91, 106, 121, 151],
  hr_lthr: [81, 90, 94, 100, 103, 107],
  hr_max: [60, 70, 80, 90],
  pace: [129, 113, 105, 99],
};

export const ZONE_NAMES: Record<ZoneModel, readonly string[]> = {
  power: ['Recovery', 'Endurance', 'Tempo', 'Threshold', 'VO2 Max', 'Anaerobic', 'Neuromuscular'],
  hr_lthr: ['Recovery', 'Aerobic', 'Tempo', 'Sub-threshold', 'Super-threshold', 'Aerobic capacity', 'Anaerobic capacity'],
  hr_max: ['Recovery', 'Endurance', 'Aerobic', 'Threshold', 'Maximum'],
  pace: ['Recovery', 'Endurance', 'Tempo', 'Threshold', 'VO2 Max'],
};

export const ZONE_LABELS: Record<ZoneModel, readonly string[]> = {
  power: ['Z1', 'Z2', 'Z3', 'Z4', 'Z5', 'Z6', 'Z7'],
  hr_lthr: ['Z1', 'Z2', 'Z3', 'Z4', 'Z5a', 'Z5b', 'Z5c'],
  hr_max: ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'],
  pace: ['Z1', 'Z2', 'Z3', 'Z4', 'Z5'],
};

export function zoneBounds(model: ZoneModel, threshold: number): number[] {
  return ZONE_PERCENTS[model].map((pct) => Math.ceil((threshold * pct) / 100));
}

function zoneIndex(value: number, bounds: readonly number[]): number {
  let index = 0;
  while (index < bounds.length && value >= at(bounds, index)) index++;
  return index;
}

export function timeInZones(samples: readonly (number | null)[], bounds: readonly number[]): number[] {
  const seconds = new Array<number>(bounds.length + 1).fill(0);
  for (const sample of samples) {
    if (sample === null) continue;
    const index = zoneIndex(sample, bounds);
    seconds[index] = at(seconds, index) + 1;
  }
  return seconds;
}

export function paceSamples(speeds: readonly (number | null)[]): (number | null)[] {
  return speeds.map((v) => (v === null || v <= 0 ? null : 1000 / v));
}

// Negating both sides turns "at or under a falling bound" into timeInZones' "at or over a rising bound".
export function paceTimeInZones(paces: readonly (number | null)[], bounds: readonly number[]): number[] {
  return timeInZones(paces.map((p) => (p === null ? null : -p)), bounds.map((b) => -b));
}
